import { useState, type FormEvent, type ReactNode } from 'react';
import { Card, ErrorBanner, Loading, Spinner } from '../../components/ui.js';
import { api } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { useAction, useAsync } from '../../lib/hooks.js';
import { AdminLayout } from './AdminLayout.js';

interface PlatformCredential {
  id: string;
  purpose: string;
  provider: string;
  label: string;
  credentialHints: Record<string, string>;
  active: boolean;
  lastTestedAt: string | null;
  lastTestOk: boolean | null;
  lastTestError: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CredentialResponse {
  data: PlatformCredential[];
  supportedProviders: Record<string, Record<string, string[]>>;
}

/**
 * Where platform mail sends from.
 *
 * The same write-only contract as payment gateways: the server stores an
 * encrypted bundle and has no endpoint that returns it, so this screen can show
 * which key is configured but never what it is. Replacing one means entering the
 * whole bundle again.
 *
 * Testing is separated from activating on purpose. Pasting an access key should
 * not be the same decision as sending every password reset through it — and
 * because an email credential cannot be verified without sending something, the
 * test button asks where to send it.
 */
export function AdminEmailPage(): ReactNode {
  return (
    <AdminLayout>{(staff) => <Email canWrite={staff.role === 'superadmin'} />}</AdminLayout>
  );
}

function Email({ canWrite }: { canWrite: boolean }): ReactNode {
  const credentials = useAsync(
    () => api.get<CredentialResponse>('/api/admin/platform-credentials?purpose=email'),
    [],
  );
  const { run, pending, error } = useAction();
  const [showForm, setShowForm] = useState(false);
  const [rotating, setRotating] = useState<string | null>(null);
  const [testing, setTesting] = useState<string | null>(null);

  if (credentials.loading) return <Loading />;

  const providers = credentials.data?.supportedProviders['email'] ?? {};
  const list = credentials.data?.data ?? [];

  async function activate(id: string): Promise<void> {
    await run(async () => {
      await api.post(`/api/admin/platform-credentials/${id}/activate`);
      credentials.reload();
    });
  }

  async function remove(id: string): Promise<void> {
    await run(async () => {
      await api.delete(`/api/admin/platform-credentials/${id}`);
      credentials.reload();
    });
  }

  const active = list.find((credential) => credential.active);

  return (
    <div className="stack">
      <ErrorBanner error={credentials.error} />
      <ErrorBanner message={error} />

      <div className="banner banner-warning">
        Credentials are encrypted before they are stored and cannot be read back through any
        endpoint — only replaced. Keep a copy wherever you normally keep secrets.
      </div>

      {!active && (
        <div className="banner">
          No credential is active, so platform mail falls back to whatever the deployment’s
          environment configures. Password reset does not work until one of them sends.
        </div>
      )}

      {list.length === 0 ? (
        <Card>
          <div className="empty">
            <h2>No email credential configured</h2>
            <p className="muted small">
              Add one to change where platform mail sends from without a redeploy. For Amazon SES
              you need an IAM user with <span className="mono">ses:SendEmail</span>, its access key
              pair, the region your identity is verified in, and a verified From address.
            </p>
          </div>
        </Card>
      ) : (
        list.map((credential) => (
          <Card key={credential.id}>
            <div className="row">
              <div>
                <div className="row" style={{ gap: '0.5rem' }}>
                  <strong>{credential.label}</strong>
                  <span className="badge">{credential.provider}</span>
                  {credential.active && <span className="badge badge-success">Active</span>}
                </div>
                <p className="small faint" style={{ marginTop: '0.3rem' }}>
                  Added {formatDate(credential.createdAt)}
                </p>
              </div>
              {canWrite && (
                <div className="button-row" style={{ marginLeft: 'auto' }}>
                  {!credential.active && (
                    <button
                      type="button"
                      className="button-secondary"
                      disabled={pending}
                      onClick={() => void activate(credential.id)}
                    >
                      Make active
                    </button>
                  )}
                  {credential.active && (
                    <button
                      type="button"
                      className="button-secondary"
                      disabled={pending}
                      onClick={() =>
                        setTesting(testing === credential.id ? null : credential.id)
                      }
                    >
                      Send a test
                    </button>
                  )}
                  <button
                    type="button"
                    className="button-secondary"
                    disabled={pending}
                    onClick={() => setRotating(rotating === credential.id ? null : credential.id)}
                  >
                    Replace credentials
                  </button>
                  {!credential.active && (
                    <button
                      type="button"
                      className="button-danger"
                      disabled={pending}
                      onClick={() => void remove(credential.id)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              )}
            </div>

            <table style={{ marginTop: '0.75rem' }}>
              <tbody>
                {Object.entries(credential.credentialHints).map(([field, hint]) => (
                  <tr key={field}>
                    <td className="muted small">{field}</td>
                    <td className="mono">{hint}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {credential.lastTestedAt ? (
              <p className="small" style={{ marginTop: '0.6rem' }}>
                {credential.lastTestOk ? (
                  <span className="badge badge-success">Test sent</span>
                ) : (
                  <span className="badge badge-danger">Failed</span>
                )}{' '}
                <span className="faint">tested {formatDate(credential.lastTestedAt)}</span>
                {credential.lastTestError && (
                  <span className="faint"> — {credential.lastTestError}</span>
                )}
              </p>
            ) : (
              <p className="small faint" style={{ marginTop: '0.6rem' }}>
                Never tested.
              </p>
            )}

            {testing === credential.id && (
              <TestForm
                credentialId={credential.id}
                onDone={() => {
                  setTesting(null);
                  credentials.reload();
                }}
                onCancel={() => setTesting(null)}
              />
            )}

            {rotating === credential.id && (
              <RotateForm
                credentialId={credential.id}
                fields={providers[credential.provider] ?? []}
                onDone={() => {
                  setRotating(null);
                  credentials.reload();
                }}
                onCancel={() => setRotating(null)}
              />
            )}
          </Card>
        ))
      )}

      {canWrite &&
        (showForm ? (
          <CredentialForm
            providers={providers}
            onDone={() => {
              setShowForm(false);
              credentials.reload();
            }}
            onCancel={() => setShowForm(false)}
          />
        ) : (
          <div>
            <button type="button" className="button-primary" onClick={() => setShowForm(true)}>
              Add an email credential
            </button>
          </div>
        ))}
    </div>
  );
}

/** Fields that are identifiers rather than secrets, so may be shown as typed. */
const VISIBLE_FIELDS = new Set(['accessKeyId', 'region', 'fromAddress']);

function CredentialFields({
  fields,
  values,
  onChange,
  idPrefix,
}: {
  fields: string[];
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
  idPrefix: string;
}): ReactNode {
  return (
    <>
      {fields.map((field) => (
        <div key={field}>
          <label htmlFor={`${idPrefix}-${field}`}>{field}</label>
          <input
            id={`${idPrefix}-${field}`}
            /*
             * A secret is a password field: the browser should not offer to
             * remember it, and it should not sit legible on a shared screen.
             * Identifiers stay visible so the operator can see what they pasted.
             */
            type={VISIBLE_FIELDS.has(field) ? 'text' : 'password'}
            autoComplete="off"
            value={values[field] ?? ''}
            onChange={(e) => onChange({ ...values, [field]: e.target.value })}
            required
          />
        </div>
      ))}
    </>
  );
}

function CredentialForm({
  providers,
  onDone,
  onCancel,
}: {
  providers: Record<string, string[]>;
  onDone: () => void;
  onCancel: () => void;
}): ReactNode {
  const providerNames = Object.keys(providers);
  const [provider, setProvider] = useState(providerNames[0] ?? 'ses');
  const [label, setLabel] = useState('');
  const [activate, setActivate] = useState(false);
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const { run, pending, error } = useAction();

  const fields = providers[provider] ?? [];

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    await run(async () => {
      await api.post('/api/admin/platform-credentials', {
        purpose: 'email',
        provider,
        label,
        credentials,
        activate,
      });
      onDone();
    });
  }

  return (
    <Card title="Add an email credential">
      <form onSubmit={(e) => void submit(e)} className="stack">
        <ErrorBanner message={error} />

        <div>
          <label htmlFor="email-provider">Provider</label>
          <select
            id="email-provider"
            value={provider}
            onChange={(e) => {
              setProvider(e.target.value);
              setCredentials({});
            }}
          >
            {providerNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="email-label">Label</label>
          <input
            id="email-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="SES (Mumbai)"
            required
          />
        </div>

        <CredentialFields
          fields={fields}
          values={credentials}
          onChange={setCredentials}
          idPrefix="email-new"
        />

        <label className="row" style={{ gap: '0.5rem' }}>
          <input
            type="checkbox"
            checked={activate}
            onChange={(e) => setActivate(e.target.checked)}
            style={{ width: 'auto' }}
          />
          <span>Send platform mail through this immediately</span>
        </label>

        <div className="button-row">
          <button type="submit" className="button-primary" disabled={pending}>
            {pending && <Spinner />}
            Save credential
          </button>
          <button type="button" className="button-secondary" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </Card>
  );
}

function RotateForm({
  credentialId,
  fields,
  onDone,
  onCancel,
}: {
  credentialId: string;
  fields: string[];
  onDone: () => void;
  onCancel: () => void;
}): ReactNode {
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const { run, pending, error } = useAction();

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    await run(async () => {
      await api.post(`/api/admin/platform-credentials/${credentialId}/credentials`, {
        credentials,
      });
      onDone();
    });
  }

  return (
    <form onSubmit={(e) => void submit(e)} className="stack" style={{ marginTop: '0.75rem' }}>
      <ErrorBanner message={error} />
      <p className="small muted">
        Every field is required. The whole bundle is replaced, not merged — a partly-updated
        credential set is the state you least want to be in while something is failing. The
        recorded test result is cleared, because a pass against the previous key says nothing about
        this one.
      </p>

      <CredentialFields
        fields={fields}
        values={credentials}
        onChange={setCredentials}
        idPrefix={`rotate-${credentialId}`}
      />

      <div className="button-row">
        <button type="submit" className="button-primary" disabled={pending}>
          {pending && <Spinner />}
          Replace credentials
        </button>
        <button type="button" className="button-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function TestForm({
  credentialId,
  onDone,
  onCancel,
}: {
  credentialId: string;
  onDone: () => void;
  onCancel: () => void;
}): ReactNode {
  const [to, setTo] = useState('');
  const { run, pending, error } = useAction();

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    await run(async () => {
      await api.post(`/api/admin/platform-credentials/${credentialId}/test`, { to });
      onDone();
    });
  }

  return (
    <form onSubmit={(e) => void submit(e)} className="stack" style={{ marginTop: '0.75rem' }}>
      <ErrorBanner message={error} />
      <div>
        <label htmlFor={`test-${credentialId}`}>Send a test message to</label>
        <input
          id={`test-${credentialId}`}
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="you@example.com"
          required
        />
        <p className="small faint" style={{ marginTop: '0.3rem' }}>
          A real message is sent — there is no way to verify an email credential without one. A
          sandboxed SES account can only send to addresses verified in that account, so use one of
          those until the sandbox is lifted.
        </p>
      </div>

      <div className="button-row">
        <button type="submit" className="button-primary" disabled={pending}>
          {pending && <Spinner />}
          Send test
        </button>
        <button type="button" className="button-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
